<!--
  - Copyright (c) 2022-2023. Haodong JU
  -->

<template>
  <large-base-card>
    <div>
      <div
        class="text-h4 font-weight-bold"
      >
        {{ $t('sales') }}
      </div>
      <div class="text--secondary text-body-2">
        {{ $t('SalesDevelopment') }}
      </div>
    </div>
    <div
      :class="$vuetify.breakpoint.sm ? 'mt-2' : ''"
      :style="$vuetify.breakpoint.smAndUp ? 'display: grid;grid-template-columns: minmax(0,0.6fr) minmax(0,1fr);grid-gap: 24px' : ''"
    >
      <div class="d-flex align-center justify-center text-body-2">
        <div>
          <div
            v-if="!graphReady"
            class="d-flex align-center justify-center"
            style="height: 400px"
          >
            <v-progress-circular
              class="mt-n7"
              indeterminate
            />
          </div>
          <div
            v-else
            :style="$vuetify.breakpoint.smAndUp ? 'width: min-content' : 'width: 100%'"
          >
            <div
              :class="$vuetify.breakpoint.smAndDown ? 'text-h2' : 'text-h1'"
              class="font-weight-bold text-no-wrap"
              style="width: fit-content;"
            >
              {{ restaurantList?.totalFinishOrder | priceDisplay }}
            </div>
            <div>
              {{ $t('todayTurnoverIncludeUnpaid') }}
            </div>
          </div>
        </div>
      </div>
      <div>
        <div
          v-if="!graphReady"
          class="d-flex align-center justify-center"
          style="height: 400px"
        >
          <v-progress-circular
            class="mt-n7"
            indeterminate
          />
        </div>
        <apex-chart
          v-else
          :options="options"
          :series="series"
          type="bar"
          width="100%"
        />
      </div>
    </div>
  </large-base-card>
</template>
<script>

import dayjs from 'dayjs'
import LargeBaseCard from '@/views/widget/commonDecoration/LargeBaseCard.vue'
import i18n from '../../../../i18n'
import Vue from 'vue'
import { groupBy, keyBy } from 'lodash'
import { getFlattenedArray } from '@/model/dataLayer/service/MultipleStores'

export default {
  components: {
    LargeBaseCard
  },
  props: {
    restaurantList: {},
    label: {
      type: String,
      default: ''
    },
    date: {}
  },
  data: function () {
    return {
      info: {
        durationTimeData: [],
        spanTurnover: { result: [] }
      },
      toggleYaxsis: false,
      graphReady: false,
      series: [],
      options: {}
    }
  },
  watch: {
    restaurantList: {
      immediate: true,
      deep: true,
      handler (val) {
        this.reloadData()
      }
    },
    date: {
      handler: function (val) {
        this.reloadData()
      }
    },
    '$vuetify.breakpoint.smAndDown': {
      handler: function (val) {
        this.reloadData()
      }
    },
    '$vuetify.breakpoint.smAndUp': {
      handler: function (val) {
        this.reloadData()
      }
    }
  },
  async mounted () {
    await this.initGraph()
  },
  methods: {
    getDataFromTurnoverData (list) {
      return list?.map(d => d.spanTotal) ?? []
    },
    getRealData (list, timespan) {
      const deviceIdSpanTurnover = groupBy(list, 'deviceId')
      return Object.entries(deviceIdSpanTurnover).map(l => {
        const deviceId = l[0]
        const turnovers = l[1]
        const smallSelectorTurnover = keyBy(turnovers, 'smallSelector')
        return {
          name: deviceId,
          data: timespan.map(t => {
            return (smallSelectorTurnover[t]?.spanTotal ?? '0')
          })
        }
      })
    },
    async reloadData () {
      await this.initGraph()
    },
    initGraph: async function () {
      this.graphReady = false
      const info = getFlattenedArray(this.restaurantList, 'spanTurnover')
      const groupByInfo = groupBy(info, 'smallSelector')
      const timespanInfo = Object.keys(groupByInfo)
      const dataList = Object.values(groupByInfo).flat()
      const realData = this.getRealData(dataList, timespanInfo)
      const categories = timespanInfo.map(d => {
        let res
        switch (this.restaurantList.smallSelector) {
          case 'hour':
            res = d + ':00'
            break
          case 'week':
            // res = `第${d.smallSelector + 1}周`
            res = this.$i18n.tc('no_week', d)
            break
          case 'date':
            res = dayjs(d).format('DD.MM')
            break
          case 'month':
          default:
            res = dayjs().set('month', d - 1).format('MMM')
        }
        return res
      }) ?? []
      const data = this.getDataFromTurnoverData(timespanInfo)

      this.series = data.length !== 0
        ? realData
        : [
            {
              name: i18n.t('sales'),
              data: [0, 0, 0]
            }
          ]

      const maxNumber = Math.max(...data) ?? 1
      const tooLowNumberArr = []
      data.forEach((item, index) => {
        const f = parseFloat(item)
        // eslint-disable-next-line no-mixed-operators
        if (f !== 0 && (maxNumber / f > 10) || f <= 0) {
          tooLowNumberArr.push(index)
        }
      })

      this.options = {
        colors: ['#ef476f', '#ffd166', '#06d6a0', '#118ab2', '#073b4c'],
        stacked: true,
        plotOptions: {
          bar: {
            dataLabels: {
              position: 'top' // top, center, bottom
            }
          }
        },
        chart: {
          type: 'bar',
          stacked: true,
          toolbar: {
            show: false
          },
          zoom: {
            enabled: true
          }
        },
        dataLabels: {
          enabled: true,
          offsetY: -20,
          style: {
            fontSize: '12px',
            fontFamily: '"Noto Sans SC", "Montserrat", sans-serif',
            fontWeight: '500',
            lineHeight: '20px',
            letterSpacing: '0.286px',
            width: '20px',
            colors: ['#304758']
          },
          formatter: function (price, val) {
            const res = parseFloat(price).toFixed(2)
            return Vue.filter('priceDisplay')(res)
          }
        },
        tooltip: {
          enabled: true,
          y: {
            formatter: function (price, y) {
              const res = parseFloat(price).toFixed(2)
              return Vue.filter('priceDisplay')(res)
            }
          }
        },
        xaxis: {
          categories
        },
        yaxis: {
          show: this.$vuetify.breakpoint.smAndDown ? this.$vuetify.breakpoint.smAndUp ? !this.toggleYaxsis : this.toggleYaxsis : true
        }
      }
      this.$emit('alreadyReload')
      this.graphReady = true
    }
  }
}
</script>

<style lang="scss" scoped>
.chart-wrap {
  max-width: 560px;
  max-height: 280px;
}
</style>
